import React, { useEffect, useState } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useSpaPageViews } from './lib/useSpaPageViews';
import { grantConsent, hasAcceptedCookies, CONSENT_COOKIE } from './lib/analytics';
import Home from './components/public/home/Home';
import About from './components/public/about/About';
import Contact from './components/public/contact/Contact';
import PBIOverview from './components/public/solutions/pbi-development/Overview';
import DEOverview from './components/public/solutions/data-engineering/Overview';
import Pricing from './components/public/pricing/Pricing';
import Delivery from './components/public/delivery/Delivery';
import ScrollToTopButton from './components/public/global/ScrollToTopButton';
import CookieConsent from "react-cookie-consent";
import PrivacyPolicy from './components/public/privacy/Privacy';
import SemanticHR from './components/public/solutions/semantic-hr/Overview';
import NotFound from './components/public/not-found/NotFound';
import SitePasswordGate from './components/public/global/SitePasswordGate';
import { updateRoutes } from './components/public/updates/updateRoutes';

/** Preserve query + hash when redirecting legacy routes to their new names. */
function RedirectWithHash({ to }) {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function App() {
    const location = useLocation();
    // Consent Mode v2: default is denied in index.html. Returning visitors who
    // accepted get the grant restored on load; Decline leaves it denied.
    const [analyticsConsent, setAnalyticsConsent] = useState(hasAcceptedCookies);
    useEffect(() => {
        if (analyticsConsent) grantConsent();
    }, [analyticsConsent]);
    useSpaPageViews(location.pathname, location.search, analyticsConsent);

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            // Wait a tick for the target route to render
            const t = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 80);
            return () => clearTimeout(t);
        }
        window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    }, [location.pathname, location.hash]);

    return (
        <SitePasswordGate>
        <div className="min-w-0 max-w-full">
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/company" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/interactive-analytics" element={<PBIOverview />} />
                <Route path="/platform-engineering" element={<DEOverview />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/delivery" element={<Delivery />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/semantic-hr" element={<SemanticHR />} />
                {updateRoutes.map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                ))}

                {/* Legacy route redirects: keep old links/CTAs working */}
                <Route path="/powerbi-development" element={<RedirectWithHash to="/interactive-analytics" />} />
                <Route path="/data-engineering" element={<RedirectWithHash to="/platform-engineering" />} />
                <Route path="/sports-intelligence" element={<RedirectWithHash to="/platform-engineering" />} />

                {/* Unknown paths render a client 404 (noindex). Sitemap HTML prerender at CloudFront/S3 is Platform Option A, not this app. */}
                <Route path="*" element={<NotFound />} />
            </Routes>
            {!(location.pathname.startsWith('/individual')) && <ScrollToTopButton showNote={false} />}
            <CookieConsent
                location="bottom"
                buttonText="Accept"
                declineButtonText="Decline"
                enableDeclineButton
                cookieName={CONSENT_COOKIE}
                onAccept={() => setAnalyticsConsent(true)}
                containerClasses="cookie-consent-bar"
                contentClasses="cookie-consent-copy"
                buttonWrapperClasses="cookie-consent-actions"
                buttonClasses="cookie-consent-accept bg-brand-700 hover:bg-brand-800 active:bg-brand-900 transition-colors"
                declineButtonClasses="cookie-consent-decline"
                style={{
                    background: "#FFFFFF",
                    color: "#374151",
                    borderTop: "1px solid #E5E7EB",
                    boxShadow: "0 -4px 14px rgba(15, 23, 42, 0.06)",
                    padding: "10px 12px",
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "nowrap",
                    alignItems: "stretch",
                    gap: "8px",
                    maxWidth: "100%",
                }}
                contentStyle={{
                    flex: "0 0 auto",
                    margin: "0",
                    padding: "0",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                }}
                buttonStyle={{
                    color: "#fff",
                    // Background comes from brand-700/800 tokens in buttonClasses (WCAG 1.4.3);
                    // undefined clears the library default so the classes apply.
                    background: undefined,
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontWeight: 500,
                    margin: 0,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                }}
                declineButtonStyle={{
                    color: "#374151",
                    background: "#F5F7FA",
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontWeight: 500,
                    border: "1px solid #E5E7EB",
                    margin: 0,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                }}
                expires={365}
            >
                We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.{" "}
                <a href="/privacy-policy" className="inline-flex min-h-[24px] items-center text-brand-700 underline">Learn more</a>
            </CookieConsent>
        </div>
        </SitePasswordGate>
    );
}

export default App;
